import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ListTree, Plus } from 'lucide-react'
import { v7 as uuidv7 } from 'uuid'
import { useSubTasks } from '../hooks/useSubTasks'
import { useCreateSubTask } from '../hooks/useCreateSubTask'
import { useProjects } from '../hooks/useProjects'
import { resolveTaskIdentifier } from '../utils/taskIdentifier'
import { isClosedProgressType } from '../constants'
import { AddTaskInput } from './AddTaskInput'
import { SubTaskItem } from './SubTaskItem'
import { SubTaskQuickAdd } from './SubTaskQuickAdd'
import type { SubTaskDraft } from './SubTaskQuickAdd'
import type { KeyboardEvent } from 'react'
import type { SubTaskReorderEvent } from './SubTaskItem'
import type { SubTaskSectionProps } from '../type'
import type { RecurrencePattern } from '@/lib/types/recurrence'
import { buildPlainTextEditorStateJSON } from '@/components/editor/TiptapRichTextEditor'
import { Button } from '@/components/ui/button'
import { Collapsible, CollapsibleContent } from '@/components/ui/collapsible'
import { CollapsibleSectionHeader } from '@/components/app/layout/collapsible-section-header'
import {
  useTask,
  useTaskContentMutations,
  useTaskLabelMutations,
  useTaskMutations,
  useTaskRelationMutations,
} from '@/lib/db/rxdb/hooks'
import { createTaskWithLabels } from '@/lib/sync/taskActions'
import { RELATION_TYPES } from '@/lib/types/taskRelation'
import useLoginStore from '@/store/LoginStore'
import { compareRanks, generateRankBetween } from '@/lib/utils/lexorank'
import { rebalanceSortOrders } from '@/lib/utils/sortOrder'
import { useAllLabels } from '@/lib/hooks/useLabels'
import { useAllLabelGroups } from '@/lib/hooks/useLabelGroups'
import { useMembers } from '@/lib/db/rxdb/hooks'
import { useWorkspaceMode } from '@/hooks/useWorkspaceMode'
import { memberAvatarSrc } from '@/lib/utils/memberAvatar'
import {
  confirmUpload,
  getPresignedUploadUrl,
} from '@/lib/api/features/attachments'
import { ENTITY_TYPES } from '@/lib/constants/entityTypes'
import { getUploadContentType } from '@/lib/utils/fileMime'

export function SubTaskSection({
  parentId,
  workspaceId,
  projectId,
  defaultStatusId,
  statuses,
  mode = 'list',
  onSubTaskClick,
  statusMode,
  onExpandedChange,
}: SubTaskSectionProps) {
  const { subTasks, isLoading } = useSubTasks(parentId, workspaceId)
  const userId = useLoginStore((state) => state.userId)
  const { createTask, updateTask } = useTaskMutations()
  const { createTaskLabel } = useTaskLabelMutations()
  const { createTaskContent } = useTaskContentMutations()
  const { createTaskRelation } = useTaskRelationMutations()
  const { task: parentTask } = useTask(parentId ?? undefined)
  const { t } = useTranslation('tasks')
  const [isAddingSubTask, setIsAddingSubTask] = useState(false)
  // Quick add is a one-line row (design 28b); the full form stays one click away.
  const [isFullForm, setIsFullForm] = useState(false)
  const [prefillName, setPrefillName] = useState('')
  const createSubTask = useCreateSubTask(parentId, workspaceId)
  const { projects } = useProjects()
  const { members } = useMembers()
  const { isPersonal } = useWorkspaceMode()
  const [isOpen, setIsOpen] = useState(true)
  const [draggingId, setDraggingId] = useState<string | null>(null)
  const [dropIndicator, setDropIndicator] = useState<{
    id: string
    position: 'before' | 'after'
  } | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const isExpanded = subTasks.length > 0 || isAddingSubTask
  useEffect(() => {
    onExpandedChange?.(isExpanded)
  }, [isExpanded, onExpandedChange])

  // Fetch labels for the workspace
  const { labels } = useAllLabels(workspaceId)
  const { labelGroups } = useAllLabelGroups(workspaceId)

  // State for sub-task properties (like Add Task)
  const [startDate, setStartDate] = useState<Date | undefined>()
  const [dueDate, setDueDate] = useState<Date | undefined>()
  const [selectedStatusId, setSelectedStatusId] = useState<string | null>(
    defaultStatusId ?? null,
  )
  const [selectedPriority, setSelectedPriority] = useState<number>(0)
  const [selectedUserId, setSelectedUserId] = useState<string | null>(userId)
  const [isSomeday, setIsSomeday] = useState(false)
  const [description, setDescription] = useState('')
  const [selectedLabelIds, setSelectedLabelIds] = useState<Array<string>>([])
  const [selectedFiles, setSelectedFiles] = useState<Array<File>>([])
  const [recurrencePattern, setRecurrencePattern] =
    useState<RecurrencePattern | null>(null)

  useEffect(() => {
    if (isAddingSubTask && isFullForm && inputRef.current) {
      if (prefillName) inputRef.current.value = prefillName
      inputRef.current.focus()
    }
  }, [isAddingSubTask, isFullForm, prefillName])

  useEffect(() => {
    setSelectedStatusId(defaultStatusId ?? null)
  }, [defaultStatusId])

  const handleAddSubTaskClick = useCallback(() => {
    setIsAddingSubTask(true)
  }, [])

  const handleCancelAdd = useCallback(() => {
    setIsAddingSubTask(false)
    setIsFullForm(false)
    setPrefillName('')
    if (inputRef.current) {
      inputRef.current.value = ''
    }
    // Reset to defaults
    setStartDate(undefined)
    setDueDate(undefined)
    setSelectedStatusId(defaultStatusId ?? null)
    setSelectedPriority(0)
    setSelectedUserId(userId)
    setIsSomeday(false)
    setDescription('')
    setSelectedLabelIds([])
    setSelectedFiles([])
    setRecurrencePattern(null)
  }, [defaultStatusId, userId])

  const handleCreateSubTask = useCallback(async () => {
    const subTaskName = inputRef.current?.value.trim()

    if (!subTaskName) {
      return
    }

    if (!parentId || !workspaceId || !selectedStatusId || !userId) {
      return
    }

    try {
      const taskId = uuidv7()
      const lastSortOrder =
        subTasks.length > 0
          ? (subTasks[subTasks.length - 1]?.sortOrder ?? null)
          : null
      const nextSortOrder = generateRankBetween(lastSortOrder, null)

      // Description is already in Tiptap JSON format from the editor
      // If it's empty, use empty editor state
      const editorJson = description || buildPlainTextEditorStateJSON('')

      // Extract plain text from editor JSON for search/display
      let plainText = ''
      try {
        const parsed = JSON.parse(editorJson)
        const textNode = parsed.content?.[0]?.content?.[0]
        plainText = textNode?.text || ''
      } catch {
        plainText = ''
      }

      // Create subtask (description fields will be extracted and used for TaskContent)
      const newSubTask = {
        id: taskId,
        name: subTaskName,
        description: editorJson, // Used for content creation only
        descriptionFormat: 'tiptap',
        descriptionLength: editorJson.length,
        descriptionPlainText: plainText || null,
        sortOrder: nextSortOrder,
        projectId: projectId,
        userId: selectedUserId ?? userId,
        dueDateType: dueDate ? 'datetime' : null,
        dueDate: dueDate ? dueDate.getTime() : null,
        priority: selectedPriority,
        isSomeday: isSomeday,
        isAllDay: true,
        statusId: selectedStatusId,
        parentId: parentId,
        statusName: '',
        statusColor: '#gray',
      }

      // Use optimistic action for multi-collection mutation
      await createTaskWithLabels({
        task: newSubTask,
        labelIds: selectedLabelIds,
        createTask: createTask,
        createTaskLabel: createTaskLabel,
        createTaskContent: createTaskContent,
        labels,
        labelGroups,
      })

      // Create parent_child relation
      await createTaskRelation({
        sourceTaskId: parentId,
        sourceProjectId: projectId ?? null,
        sourceTaskName: parentTask?.name ?? '',
        sourceIdentifier: (parentTask as any)?.identifier ?? null,
        targetTaskId: taskId,
        targetProjectId: projectId ?? null,
        targetTaskName: subTaskName,
        targetIdentifier: null,
        relationType: RELATION_TYPES.PARENT_CHILD,
      })

      // Upload attachments if any
      if (selectedFiles.length > 0 && workspaceId) {
        // Upload files in the background (don't block sub-task creation)
        void Promise.all(
          selectedFiles.map(async (file) => {
            try {
              const uploadContentType = getUploadContentType(file)
              // Get presigned URL
              const presignedData = await getPresignedUploadUrl(
                workspaceId,
                ENTITY_TYPES.WORK_ITEM,
                taskId,
                {
                  fileName: file.name,
                  fileType: uploadContentType,
                  fileSizeBytes: file.size,
                },
              )

              // Upload to R2
              const uploadResponse = await fetch(presignedData.uploadUrl, {
                method: 'PUT',
                body: file,
                headers: {
                  'Content-Type': uploadContentType,
                },
              })

              if (!uploadResponse.ok) {
                throw new Error(`Upload failed: ${uploadResponse.statusText}`)
              }

              // Confirm upload
              await confirmUpload(workspaceId, presignedData.attachmentId)
            } catch (error) {
              // Error handling
              // Don't fail sub-task creation if attachment upload fails
            }
          }),
        )
      }

      // Reset to defaults
      if (inputRef.current) {
        inputRef.current.value = ''
      }
      setStartDate(undefined)
      setDueDate(undefined)
      setSelectedStatusId(defaultStatusId ?? null)
      setSelectedPriority(0)
      setSelectedUserId(userId)
      setIsSomeday(false)
      setDescription('')
      setSelectedLabelIds([])
      setSelectedFiles([])
      setRecurrencePattern(null)
      setIsAddingSubTask(false)
      setIsFullForm(false)
      setPrefillName('')
    } catch (error) {
      // Error handling
    }
  }, [
    parentId,
    workspaceId,
    projectId,
    selectedStatusId,
    userId,
    subTasks,
    startDate,
    dueDate,
    selectedPriority,
    selectedUserId,
    isSomeday,
    description,
    selectedLabelIds,
    selectedFiles,
    recurrencePattern,
    defaultStatusId,
    createTaskRelation,
    parentTask,
  ])

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Escape') {
        handleCancelAdd()
      }
    },
    [handleCancelAdd],
  )

  const handleReorder = useCallback(
    (event: SubTaskReorderEvent) => {
      const sourceIndex = subTasks.findIndex(
        (item) => item.id === event.sourceId,
      )
      if (sourceIndex === -1) {
        return
      }

      const moved = subTasks[sourceIndex]
      // Indexing gives `T | undefined` — bail rather than reordering against a
      // phantom item; this also lets every `moved.*` read below narrow.
      if (!moved) return
      const reordered = [...subTasks]
      reordered.splice(sourceIndex, 1)

      const targetIndex = reordered.findIndex(
        (item) => item.id === event.targetId,
      )
      if (targetIndex === -1) {
        return
      }

      let insertIndex = targetIndex
      if (sourceIndex < targetIndex) {
        insertIndex -= 1
      }

      if (event.dropPosition === 'after') {
        insertIndex += 1
      }

      insertIndex = Math.max(0, Math.min(reordered.length, insertIndex))
      reordered.splice(insertIndex, 0, moved)

      const hasChanged = reordered.some(
        (item, index) => item.id !== subTasks[index]?.id,
      )
      if (!hasChanged) {
        return
      }

      const movedIndex = reordered.findIndex((item) => item.id === moved.id)
      const beforeSort =
        movedIndex > 0 ? (reordered[movedIndex - 1]?.sortOrder ?? null) : null
      const afterSort =
        movedIndex < reordered.length - 1
          ? (reordered[movedIndex + 1]?.sortOrder ?? null)
          : null

      const shouldRebalance =
        beforeSort !== null &&
        afterSort !== null &&
        compareRanks(beforeSort, afterSort) >= 0

      if (shouldRebalance) {
        rebalanceSortOrders(reordered, updateTask)
        return
      }

      const nextSortOrder = generateRankBetween(beforeSort, afterSort)
      if (nextSortOrder === moved.sortOrder) {
        return
      }

      try {
        void updateTask(moved.id, {
          sortOrder: nextSortOrder,
        })
      } catch (error) {
        // Error handling
      }
    },
    [subTasks, updateTask],
  )

  const handleQuickAdd = useCallback(
    async (draft: SubTaskDraft) => {
      if (!defaultStatusId) return
      await createSubTask({ name: draft.name, statusId: defaultStatusId, userId: draft.userId, dueDate: draft.dueDate })
    },
    [createSubTask, defaultStatusId],
  )

  const projectKey = useMemo(
    () => projects.find((p) => p.id === projectId)?.projectKey ?? null,
    [projects, projectId],
  )

  const doneCount = useMemo(() => {
    const progressById = new Map(statuses.map((status) => [status.id, status.progressType]))
    return subTasks.filter((task) => isClosedProgressType(progressById.get(task.statusId))).length
  }, [subTasks, statuses])

  const assigneeFor = (assigneeId: string | null | undefined) => {
    if (!assigneeId || isPersonal) return null
    const member = members.find((m) => m.userId === assigneeId)
    if (!member) return null
    const name = member.name ?? member.email ?? ''
    return { name, initials: name.slice(0, 2).toUpperCase(), src: memberAvatarSrc(member) }
  }

  if (isLoading) {
    return null
  }

  if (!parentId || !workspaceId || !defaultStatusId) {
    return null
  }

  const addSubTaskButton = (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      data-testid="add-subtask"
      onClick={handleAddSubTaskClick}
      className="-ml-2 gap-1.5 text-muted-foreground hover:text-accent-foreground"
    >
      <ListTree className="size-4" />
      Add sub-task
    </Button>
  )

  const quickAdd = (
    <SubTaskQuickAdd
      onSubmit={handleQuickAdd}
      onCancel={handleCancelAdd}
      onMoreOptions={(name) => {
        setPrefillName(name)
        setIsFullForm(true)
      }}
      showAssign={!isPersonal}
      projectId={projectId ?? null}
      allowSuperAgent={projects.find((p) => p.id === projectId)?.superAgentEnabled ?? false}
    />
  )

  const addSubTaskInput = !isAddingSubTask ? null : !isFullForm ? quickAdd : (
    <AddTaskInput
      ref={inputRef}
      variant="full"
      placeholder="Sub-task name"
      descriptionPlaceholder="Add description..."
      outlined
      showDescription
      showProject={false}
      icon={<ListTree className="size-4" />}
      onKeyDown={handleKeyDown}
      descriptionValue={description}
      onDescriptionChange={setDescription}
      startDate={startDate}
      dueDate={dueDate}
      onStartDateChange={setStartDate}
      onDueDateChange={setDueDate}
      selectedPriority={selectedPriority}
      onPriorityChange={setSelectedPriority}
      selectedUserId={selectedUserId}
      onUserChange={setSelectedUserId}
      statuses={statuses}
      selectedStatusId={selectedStatusId}
      onStatusChange={setSelectedStatusId}
      availableLabels={labels}
      labelGroups={labelGroups}
      selectedLabelIds={selectedLabelIds}
      onLabelsChange={setSelectedLabelIds}
      selectedFiles={selectedFiles}
      onFilesChange={setSelectedFiles}
      reminderOffsetMinutes={null}
      recurrencePattern={recurrencePattern}
      onRecurrenceChange={setRecurrencePattern}
      isSomeday={isSomeday}
      onSomedayChange={() => setIsSomeday((prev) => !prev)}
      onCreateClick={() => void handleCreateSubTask()}
      onCancelClick={handleCancelAdd}
    />
  )

  const subTaskItems = (
    <div className="space-y-0.5">
      {subTasks.map((subTask) => (
        <SubTaskItem
          key={subTask.id}
          subTask={subTask}
          draggingId={draggingId}
          onDragStart={setDraggingId}
          onDragEnd={() => setDraggingId(null)}
          onReorder={handleReorder}
          dropIndicator={dropIndicator}
          onDropIndicatorChange={setDropIndicator}
          onSubTaskClick={onSubTaskClick}
          statusMode={statusMode}
          identifier={resolveTaskIdentifier(subTask, projectKey) || null}
          assignee={assigneeFor(subTask.userId)}
          showAssign={!isPersonal}
        />
      ))}
    </div>
  )

  const collapsibleSubTaskList = (
    <div className="space-y-2">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleSectionHeader
          icon={<ListTree className="size-4" />}
          title={t('detailHeader.subTasks')}
          count={subTasks.length > 0 ? `${doneCount}/${subTasks.length}` : undefined}
          isOpen={isOpen}
          rightSlot={
            !isAddingSubTask && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={handleAddSubTaskClick}
                className="size-6 text-muted-foreground hover:text-foreground hover:bg-accent"
              >
                <Plus className="size-4" />
              </Button>
            )
          }
        />
        <CollapsibleContent className="space-y-1 pt-2">
          {subTaskItems}
          {addSubTaskInput ?? (
            <button
              type="button"
              onClick={handleAddSubTaskClick}
              className="flex h-8 items-center gap-2 rounded-md px-1.5 text-[13px] text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
            >
              <Plus className="size-4" />
              {t('subTask.add')}
            </button>
          )}
        </CollapsibleContent>
      </Collapsible>
    </div>
  )

  if (mode === 'button') {
    return isAddingSubTask ? addSubTaskInput : addSubTaskButton
  }

  if (mode === 'chip') {
    return isExpanded ? (
      collapsibleSubTaskList
    ) : (
      <button type="button" data-testid="add-subtask" onClick={handleAddSubTaskClick} className='inline-flex h-7 items-center gap-1.5 rounded-md bg-secondary px-2.5 text-[12.5px] text-muted-foreground transition-colors hover:text-foreground'>
        <ListTree className="size-3.5" />
        Sub-task
      </button>
    )
  }

  if (mode === 'full') {
    return subTasks.length === 0 && !isAddingSubTask
      ? addSubTaskButton
      : collapsibleSubTaskList
  }

  return subTasks.length > 0 || isAddingSubTask ? collapsibleSubTaskList : null
}
